import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../pdfSetup";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Phone, Music2, Volume2, X, ArrowUpRight, BookLock, Compass, Search, MoreHorizontal, Vibrate, RotateCcw, Shield, Check, Sparkles, Images, Map, Wind } from "lucide-react";
import { useReader, ZONES, zoneIndexForPage } from "../lib/reader";
import { YourTrail } from "../components/YourTrail";
import { SearchInside } from "../components/SearchInside";
import { Waypoint, PausePractice } from "../components/Waypoint";
import { api as readerApi } from "../lib/reader";
import { fetchBook } from "../lib/api";
import { trackAudioUrl } from "../lib/api";
import { API } from "../lib/api";
import { bookPdfUrl, restoreEntitlement, recordCodeOpen } from "../lib/api";
import { useSiteContent } from "../lib/siteContent";
import { hapticTick, setPortraitLock } from "../lib/native";
import { APPS_LIVE } from "../lib/stores";
import { FlipbookReader } from "../components/FlipbookReader";
import { ReaderControls } from "../components/ReaderControls";
import { ReaderCompanion } from "../components/ReaderCompanion";
import { UpcomingStrip } from "../components/UpcomingStrip";
import { SomaticResetStrip } from "../components/SomaticResetStrip";
import { ThumbnailScrub } from "../components/ThumbnailScrub";
import { AboutAuthor } from "../components/AboutAuthor";
import { Ecosystem } from "../components/Ecosystem";
import { TrailMap } from "../components/TrailMap";
import { PlainWordsCard } from "../components/PlainWordsCard";
import { useBookExtras } from "../lib/bookExtras";
import { ArtGalleryTeaser } from "../components/ArtGalleryTeaser";
import { OfferingsStrip } from "../components/OfferingsStrip";
import { Preface } from "../components/Preface";
import { SpatialTopography } from "../components/SpatialTopography";
import { LoadingContour } from "../components/LoadingContour";

const COVER_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_tactile-book/artifacts/suzz6jtr_Braced%20No%20More%20Cover.webp";

function computeSpreads(pageCount, isMobile) {
  if (!pageCount) return [[1]];
  if (isMobile) return Array.from({ length: pageCount }, (_, i) => [i + 1]);
  const spreads = [[1]];
  for (let p = 2; p <= pageCount; p += 2) {
    spreads.push(p + 1 <= pageCount ? [p, p + 1] : [p]);
  }
  return spreads;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 900 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

export default function BookExperience() {
  // "?fresh=1" wipes any remembered unlock so you can preview the exact
  // experience a brand-new visitor gets (free Chapter One, then the paywall).
  if (typeof window !== "undefined") {
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("fresh") === "1") {
        ["bnm-book-unlocked", "bnm-entitlement", "bnm-reader-name", "bnm-reader-email", "bnm-reader-phone", "bnm-progress"].forEach((k) => localStorage.removeItem(k));
        sessionStorage.removeItem("bnm-entered");
        const keep = sp.get("paywall") === "1" ? "?paywall=1" : "";
        window.history.replaceState(null, "", window.location.pathname + keep);
      }
    } catch (e) { /* ignore */ }
  }
  const [book, setBook] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // lead page of current spread
  const [direction, setDirection] = useState(0);
  const sessionEntered = (() => {
    try { return sessionStorage.getItem("bnm-entered") === "1"; } catch (e) { return false; }
  })();
  const readBookUnlocked = () => {
    try {
      if (localStorage.getItem("bnm-book-unlocked") === "1") return true;
      const ent = JSON.parse(localStorage.getItem("bnm-entitlement") || "null");
      return !!(ent && Array.isArray(ent.tiers) && ent.tiers.includes("book_owner"));
    } catch (e) { return false; }
  };
  const [unlocked, setUnlocked] = useState(sessionEntered);
  const [pwError, setPwError] = useState(false);
  const prefersReduced = useReducedMotion();
  const [entered, setEntered] = useState(sessionEntered);
  const [readerName, setReaderName] = useState(() => { try { return localStorage.getItem("bnm-reader-name") || ""; } catch (e) { return ""; } });
  const [readerEmail, setReaderEmail] = useState(() => { try { return localStorage.getItem("bnm-reader-email") || ""; } catch (e) { return ""; } });
  const [readerPhone, setReaderPhone] = useState(() => { try { return localStorage.getItem("bnm-reader-phone") || ""; } catch (e) { return ""; } });
  const [pwName, setPwName] = useState("");
  const [pwEmail, setPwEmail] = useState("");
  const [pwPhone, setPwPhone] = useState("");
  const [pwLeadSaved, setPwLeadSaved] = useState(false);
  const [pwLeadSaving, setPwLeadSaving] = useState(false);
  const [pwLeadErr, setPwLeadErr] = useState("");
  const [returning, setReturning] = useState(() => { try { return !!(localStorage.getItem("bnm-reader-email") && localStorage.getItem("bnm-reader-phone")); } catch (e) { return false; } });
  const [bookUnlocked, setBookUnlocked] = useState(readBookUnlocked);
  const site = useSiteContent();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [galleryNudge, setGalleryNudge] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");
  const [codeSubmitting, setCodeSubmitting] = useState(false);
  const [scrubOpen, setScrubOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [ecoOpen, setEcoOpen] = useState(false);
  const [prefaceOpen, setPrefaceOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [resumePage, setResumePage] = useState(null);
  const [bookmarkPage, setBookmarkPage] = useState(null);
  const restoredRef = useRef(false);

  // Gentle one-time nudge for book owners to discover the full Art Gallery.
  useEffect(() => {
    if (!entered || !bookUnlocked) return;
    let seen = false;
    try { seen = localStorage.getItem("bnm-gallery-nudge-seen") === "1"; } catch (e) {}
    if (seen) return;
    const t = setTimeout(() => setGalleryNudge(true), 9000);
    return () => clearTimeout(t);
  }, [entered, bookUnlocked]);
  const dismissGalleryNudge = useCallback(() => {
    setGalleryNudge(false);
    try { localStorage.setItem("bnm-gallery-nudge-seen", "1"); } catch (e) {}
  }, []);


  // Remember entry state for this browser session so navigating the ecosystem
  // ("Back to the book") returns the reader straight to their page without re-gating.
  useEffect(() => {
    if (!entered) return;
    try { sessionStorage.setItem("bnm-entered", "1"); } catch (e) {}
  }, [entered]);

  // Reader identity + progress persistence (anonymous, device-stored).
  const reader = useReader(entered);
  const [trailOpen, setTrailOpen] = useState(false);
  const [trailMapOpen, setTrailMapOpen] = useState(false);
  const [sessionPrefill, setSessionPrefill] = useState(false);
  const bookExtras = useBookExtras();
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [hapticsOn, setHapticsOn] = useState(() => {
    try { return localStorage.getItem("bnm-haptics") !== "0"; } catch (e) { return true; }
  });
  const [portraitLock, setPortraitLockState] = useState(() => {
    try { return localStorage.getItem("bnm-portrait-lock") === "1"; } catch (e) { return false; }
  });
  const hapticsRef = useRef(hapticsOn);
  useEffect(() => {
    hapticsRef.current = hapticsOn;
    try { localStorage.setItem("bnm-haptics", hapticsOn ? "1" : "0"); } catch (e) {}
  }, [hapticsOn]);
  useEffect(() => {
    try { localStorage.setItem("bnm-portrait-lock", portraitLock ? "1" : "0"); } catch (e) {}
    setPortraitLock(portraitLock);
  }, [portraitLock]);
  const [trailConfig, setTrailConfig] = useState(null);
  const [waypointZone, setWaypointZone] = useState(null);
  const [pausePractice, setPausePractice] = useState(null);
  const [greeting, setGreeting] = useState(null);
  const [restoreMode, setRestoreMode] = useState(false);
  const [restoreCode, setRestoreCode] = useState("");
  const [restoreError, setRestoreError] = useState("");
  const lastZoneIdx = useRef(null);
  const greetedRef = useRef(false);

  useEffect(() => { readerApi.config().then(setTrailConfig).catch(() => {}); }, []);

  // Always open on page 1; expose any saved backend progress as a "continue" option.
  const backendResumeRef = useRef(false);
  useEffect(() => {
    if (!reader.loaded || !reader.state || !book || backendResumeRef.current) return;
    backendResumeRef.current = true;
    const lp = reader.state.last_page || 1;
    if (lp > 1 && lp <= book.page_count) {
      setResumePage((cur) => Math.max(cur || 0, lp));
      setBookmarkPage((cur) => Math.max(cur || 0, lp));
    }
    lastZoneIdx.current = zoneIndexForPage(1);
  }, [reader.loaded, reader.state, book]);

  // Persist page + zone, and raise a Waypoint when a new zone is reached.
  useEffect(() => {
    if (!reader.loaded || !entered) return;
    const zi = zoneIndexForPage(currentPage);
    const zoneId = zi >= 0 ? ZONES[zi].id : "intro";
    const crossed = lastZoneIdx.current !== null && zi > lastZoneIdx.current && zi >= 0;
    reader.patchState((p) => {
      const next = { ...p, last_page: currentPage > 1 ? currentPage : (p.last_page || 1), zone_id: zoneId };
      if (crossed) {
        const z = ZONES[zi];
        next.zones_read = Array.from(new Set([...(p.zones_read || []), z.id]));
        next.trail_log = [...(p.trail_log || []), { type: "zone", name: z.name, at: new Date().toISOString() }];
      }
      return next;
    });
    if (crossed) setWaypointZone(ZONES[zi]);
    if (lastZoneIdx.current === null || zi !== lastZoneIdx.current) lastZoneIdx.current = zi;
  }, [currentPage, reader.loaded, entered]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return greeting — by place on the trail, never by time away.
  useEffect(() => {
    if (!entered || greetedRef.current || !reader.loaded) return;
    greetedRef.current = true;
    if (reader.isReturning) {
      const zi = zoneIndexForPage(reader.state?.last_page || 1);
      setGreeting(zi >= 0 ? `Welcome back — you were on the ${ZONES[zi].name}.` : "Welcome back to the trail.");
      setTimeout(() => setGreeting(null), 5200);
    }
  }, [entered, reader.loaded, reader.isReturning]); // eslint-disable-line react-hooks/exhaustive-deps

  const trailJump = useCallback((p) => {
    setDirection(1);
    setCurrentPage(Math.max(1, p));
    setTrailOpen(false);
  }, []);

  const submitRestore = useCallback(async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (beginMusicRef.current) beginMusicRef.current();
    try {
      const data = await readerApi.restore(restoreCode);
      reader.applyRestored(data);
      try { localStorage.setItem("bnm-unlocked", "1"); } catch (err) {}
      setUnlocked(true);
      setEntered(true);
      setRestoreError("");
    } catch (err) {
      setRestoreError("That trail code doesn't match a saved trail.");
    }
  }, [restoreCode, reader]);
  const [zoom, setZoom] = useState(1);
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 2;
  const ZOOM_STEP = 0.2;
  const zoomIn = useCallback(() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2))), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2))), []);

  const isMobile = useIsMobile();
  const containerRef = useRef(null);
  const idleTimer = useRef(null);
  const beginMusicRef = useRef(null);
  const touchStart = useRef(null);

  // Music — real tracks distributed evenly across the book; two decks for smooth crossfades.
  const deckARef = useRef(null);
  const deckBRef = useRef(null);
  const activeDeck = useRef("A");
  const playingTrack = useRef(null);
  const fadeTimers = useRef({});
  const fadeGen = useRef({});
  const startedRef = useRef(false);
  const [musicOn, setMusicOn] = useState(false);
  const [soundStarted, setSoundStarted] = useState(false);
  const [autoFollow, setAutoFollow] = useState(true);
  const [manualTrackId, setManualTrackId] = useState(null);
  const [musicPanelOpen, setMusicPanelOpen] = useState(false);
  const [trackToast, setTrackToast] = useState(null);
  const toastTimer = useRef(null);
  const [volume, setVolume] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem("bnm-volume"));
      return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.42;
    } catch (e) {
      return 0.42;
    }
  });
  const volumeRef = useRef(volume);

  const onVolumeChange = useCallback((v) => {
    const nv = Math.min(1, Math.max(0, v));
    volumeRef.current = nv;
    setVolume(nv);
    try { localStorage.setItem("bnm-volume", String(nv)); } catch (e) {}
    const activeKey = activeDeck.current;
    const a = activeKey === "A" ? deckARef.current : deckBRef.current;
    if (a && !a.paused) a.volume = nv;
  }, []);

  const tracks = book?.tracks || [];
  const totalPages = book?.page_count || 0;

  // Which track a given page falls into — even segments across the whole book.
  const segmentTrackId = useMemo(() => {
    if (!tracks.length || !totalPages) return null;
    const seg = Math.min(tracks.length - 1, Math.floor(((currentPage - 1) / totalPages) * tracks.length));
    return tracks[seg]?.id || tracks[0].id;
  }, [tracks, totalPages, currentPage]);

  const activeTrackId = autoFollow ? segmentTrackId : (manualTrackId || segmentTrackId);
  const activeTrack = tracks.find((t) => t.id === activeTrackId) || null;

  const fadeAudio = useCallback((audio, key, target, onDone) => {
    if (!audio) return;
    if (fadeTimers.current[key]) clearInterval(fadeTimers.current[key]);
    const gen = (fadeGen.current[key] || 0) + 1;
    fadeGen.current[key] = gen;
    fadeTimers.current[key] = setInterval(() => {
      const step = 0.03;
      const v = audio.volume;
      if (Math.abs(v - target) <= step) {
        audio.volume = target;
        clearInterval(fadeTimers.current[key]);
        fadeTimers.current[key] = null;
        // Ignore stale completion if a newer fade on this deck has started.
        if (fadeGen.current[key] === gen) onDone && onDone();
      } else {
        audio.volume = Math.min(1, Math.max(0, v + (target > v ? step : -step)));
      }
    }, 55);
  }, []);

  const showTrackToast = useCallback((title) => {
    if (!title) return;
    setTrackToast(title);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setTrackToast(null), 3600);
  }, []);

  const cancelFade = useCallback((key) => {
    if (fadeTimers.current[key]) {
      clearInterval(fadeTimers.current[key]);
      fadeTimers.current[key] = null;
    }
    // Invalidate any pending completion callback for this deck.
    fadeGen.current[key] = (fadeGen.current[key] || 0) + 1;
  }, []);

  const startPlayback = useCallback((trackId) => {
    const frontKey = activeDeck.current;
    const front = frontKey === "A" ? deckARef.current : deckBRef.current;
    if (!front || !trackId) return Promise.resolve(false);
    cancelFade(frontKey);
    front.src = trackAudioUrl(trackId);
    front.volume = 0;
    return front.play()
      .then(() => {
        fadeAudio(front, frontKey, volumeRef.current);
        playingTrack.current = trackId;
        try { sessionStorage.setItem("bnm-last-track", trackId); } catch (e) {}
        setSoundStarted(true);
        return true;
      })
      .catch(() => false);
  }, [fadeAudio, cancelFade]);

  // Single robust entry point: start the soundtrack from a real user gesture,
  // used by every "enter the book" path (passphrase submit, returning-reader
  // "Begin reading", and trail-code restore). Must be called synchronously
  // inside the click/submit handler so browser autoplay policy allows sound.
  const beginMusic = useCallback(() => {
    if (startedRef.current) return;
    const opener = activeTrackId || (tracks[0] && tracks[0].id);
    if (!opener) return;
    startedRef.current = true;
    startPlayback(opener).then((playing) => {
      if (playing) setMusicOn(true);
      else startedRef.current = false;
    });
  }, [activeTrackId, tracks, startPlayback]);
  beginMusicRef.current = beginMusic;

  const crossfadeTo = useCallback((trackId, announce = true) => {
    if (!trackId) return Promise.resolve(false);
    // Already playing (or already crossfading to) this track — nothing to do.
    if (playingTrack.current === trackId) return Promise.resolve(true);
    const frontKey = activeDeck.current;
    const backKey = frontKey === "A" ? "B" : "A";
    const front = frontKey === "A" ? deckARef.current : deckBRef.current;
    const back = backKey === "A" ? deckARef.current : deckBRef.current;
    if (!back) return Promise.resolve(false);
    cancelFade(backKey);
    // Claim the crossfade SYNCHRONOUSLY so a rapid follow-up call reads the
    // correct front/back decks and never leaves the old track playing.
    activeDeck.current = backKey;
    playingTrack.current = trackId;
    back.src = trackAudioUrl(trackId);
    back.volume = 0;
    // Start fading the outgoing deck down immediately (don't wait for play()).
    if (front) fadeAudio(front, frontKey, 0, () => { try { front.pause(); } catch (e) {} });
    return back.play()
      .then(() => {
        fadeAudio(back, backKey, volumeRef.current);
        try { sessionStorage.setItem("bnm-last-track", trackId); } catch (e) {}
        setSoundStarted(true);
        if (announce) {
          const t = tracks.find((x) => x.id === trackId);
          showTrackToast(t?.title);
        }
        return true;
      })
      .catch(() => false);
  }, [fadeAudio, cancelFade, tracks, showTrackToast]);

  const toggleMusic = useCallback(() => {
    if (!musicOn) {
      startPlayback(activeTrackId).then((ok) => {
        if (ok) { setMusicOn(true); startedRef.current = true; }
      });
    } else {
      fadeAudio(deckARef.current, "A", 0, () => deckARef.current && deckARef.current.pause());
      fadeAudio(deckBRef.current, "B", 0, () => deckBRef.current && deckBRef.current.pause());
      playingTrack.current = null;
      setMusicOn(false);
    }
  }, [musicOn, activeTrackId, startPlayback, fadeAudio]);

  // Autoplay Casey's Cadence the moment the URL opens (best-effort; fall back to first gesture).
  useEffect(() => {
    if (!tracks.length || startedRef.current) return;
    const opener = segmentTrackId || tracks[0].id;
    let cancelled = false;
    const begin = () => {
      if (startedRef.current) return Promise.resolve(false);
      return startPlayback(opener).then((ok) => {
        if (ok && !cancelled) { startedRef.current = true; setMusicOn(true); }
        return ok;
      });
    };
    begin().then((ok) => {
      if (ok || cancelled) return;
      const kick = () => {
        begin().then((ok2) => {
          if (ok2) {
            window.removeEventListener("pointerdown", kick);
            window.removeEventListener("keydown", kick);
            window.removeEventListener("touchstart", kick);
          }
        });
      };
      window.addEventListener("pointerdown", kick);
      window.addEventListener("keydown", kick);
      window.addEventListener("touchstart", kick);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks.length]);

  // Crossfade to the active track whenever it changes while playing.
  // Debounced so fast page-turns across chapters coalesce to the final track
  // instead of stacking several beds on top of each other.
  useEffect(() => {
    if (!musicOn || !activeTrackId) return;
    if (playingTrack.current === activeTrackId) return;
    const t = setTimeout(() => {
      if (playingTrack.current !== activeTrackId) crossfadeTo(activeTrackId);
    }, 450);
    return () => clearTimeout(t);
  }, [activeTrackId, musicOn, crossfadeTo]);

  // Quick recall: reader explicitly picks a track.
  const selectTrack = useCallback((trackId) => {
    setAutoFollow(false);
    setManualTrackId(trackId);
    if (!musicOn) {
      startPlayback(trackId).then((ok) => { if (ok) { setMusicOn(true); startedRef.current = true; } });
    } else {
      crossfadeTo(trackId);
    }
  }, [musicOn, startPlayback, crossfadeTo]);

  const enableAutoFollow = useCallback(() => {
    setAutoFollow(true);
    setManualTrackId(null);
  }, []);

  // Chapter One is free — we open the reader after a gentle name + email capture.
  const submitEntry = useCallback(
    async (e) => {
      e && e.preventDefault && e.preventDefault();
      const em = (readerEmail || "").trim().toLowerCase();
      if (!em.includes("@")) { setPwError(true); return; }
      const ph = (readerPhone || "").replace(/[^\d+]/g, "");
      if (ph.length < 7) { setPwError("phone"); return; }
      const nm = (readerName || "").trim();
      try {
        await fetch(`${API}/lead`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: em, first_name: nm, phone: (readerPhone || "").trim(), origin_url: window.location.origin }),
        });
      } catch (err) {}
      try {
        localStorage.setItem("bnm-reader-name", nm);
        localStorage.setItem("bnm-reader-email", em);
        localStorage.setItem("bnm-reader-phone", (readerPhone || "").trim());
      } catch (err) {}
      setReturning(true);
      setPwError(false);
      setUnlocked(true);
      setEntered(true);
      // Kick off music within this trusted click gesture so real browsers
      // (which block autoplay-with-sound) reliably start the soundtrack.
      beginMusic();
    },
    [readerName, readerEmail, readerPhone, beginMusic]
  );

  const submitPaywallLead = async (e) => {
    e.preventDefault();
    setPwLeadErr("");
    const em = pwEmail.trim().toLowerCase();
    if (!pwName.trim() || !em.includes("@")) { setPwLeadErr("Please add your name and a valid email."); return; }
    setPwLeadSaving(true);
    try {
      await fetch(`${API}/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: pwName.trim(), email: em, phone: pwPhone.trim(), source: "paywall_threshold", origin_url: window.location.origin }),
      });
      setPwLeadSaved(true);
    } catch (err) {
      setPwLeadErr("Something interrupted that — please try again.");
    } finally {
      setPwLeadSaving(false);
    }
  };

  // Redeem an access code (BRACEDANDLOVED or a purchased code) to unlock the full book.
  const submitAccessCode = useCallback(
    async (e) => {
      e && e.preventDefault && e.preventDefault();
      setCodeSubmitting(true); setCodeError("");
      try {
        // restoreEntitlement sends the device id and surfaces the server's
        // gentle detail (e.g. the device-limit message) as the Error message.
        const data = await restoreEntitlement(codeInput);
        const tiers = data.tiers || [];
        if (tiers.includes("book_owner")) {
          try {
            localStorage.setItem("bnm-book-unlocked", "1");
            localStorage.setItem("bnm-entitlement", JSON.stringify({ access_code: data.access_code, tiers }));
          } catch (err) {}
          setBookUnlocked(true);
          setPaywallOpen(false);
          setCodeError("");
        } else {
          setCodeError("That code doesn't unlock the full book.");
        }
      } catch (err) {
        setCodeError(err?.message || "That access code wasn't found.");
      } finally {
        setCodeSubmitting(false);
      }
    },
    [codeInput]
  );

  const pageCount = book?.page_count || 0;
  const pdfUrl = useMemo(() => {
    if (!bookUnlocked) return bookPdfUrl(null);
    let code = null;
    try { code = JSON.parse(localStorage.getItem("bnm-entitlement") || "null")?.access_code || null; } catch (e) {}
    if (!code) return bookPdfUrl(null);
    return bookPdfUrl(code);
  }, [bookUnlocked]);
  const spreads = useMemo(() => computeSpreads(pageCount, isMobile), [pageCount, isMobile]);
  const spreadIndex = useMemo(() => {
    const idx = spreads.findIndex((s) => s.includes(currentPage));
    return idx < 0 ? 0 : idx;
  }, [spreads, currentPage]);

  // Clear the bookmark ribbon once the reader turns away from the resumed spread.
  useEffect(() => {
    if (bookmarkPage == null) return;
    const inView = (spreads[spreadIndex] || []).includes(bookmarkPage);
    if (!inView) setBookmarkPage(null);
  }, [spreadIndex, spreads, bookmarkPage]);

  useEffect(() => {
    fetchBook().then(setBook).catch(() => {});
  }, []);

  // Honor a ?code= link (e.g. from the purchase confirmation email): redeem it,
  // unlock the full book and drop the reader straight in.
  const codeLinkRef = useRef(false);
  useEffect(() => {
    // Admin preview: "?paywall=1" enters a locked reader and opens the paywall.
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("paywall") === "1") {
        setEntered(true); setUnlocked(true);
        setTimeout(() => setPaywallOpen(true), 500);
        window.history.replaceState({}, "", window.location.pathname);
      }
    } catch (e) {}
  }, []);
  useEffect(() => {
    if (codeLinkRef.current) return;
    codeLinkRef.current = true;
    let code = null;
    try { code = new URLSearchParams(window.location.search).get("code"); } catch (e) {}
    if (!code) return;
    recordCodeOpen(code);
    restoreEntitlement(code)
      .then((data) => {
        const tiers = data.tiers || [];
        if (!tiers.includes("book_owner")) return;
        try {
          localStorage.setItem("bnm-book-unlocked", "1");
          localStorage.setItem("bnm-entitlement", JSON.stringify({ access_code: data.access_code, tiers }));
        } catch (e) {}
        setBookUnlocked(true);
        setUnlocked(true);
        setEntered(true);
        try { window.history.replaceState({}, "", window.location.pathname); } catch (e) {}
      })
      .catch(() => {});
  }, []);

  // Always open on page 1 — never skip ahead to the saved page. Capture any
  // saved page so a returning reader can choose to continue from it.
  useEffect(() => {
    if (!book || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const p = parseInt(localStorage.getItem("bnm-page"), 10);
      if (Number.isInteger(p) && p > 1 && p <= (book.page_count || 1)) {
        setResumePage((cur) => Math.max(cur || 0, p));
        setBookmarkPage((cur) => Math.max(cur || 0, p));
      }
    } catch (e) {}
  }, [book]);

  // Save the current page as the reader moves — but never let simply landing on
  // page 1 wipe a returning reader's saved bookmark.
  useEffect(() => {
    if (!book) return;
    if (currentPage <= 1) return;
    try { localStorage.setItem("bnm-page", String(currentPage)); } catch (e) {}
  }, [currentPage, book]);

  // Keep free readers inside Chapter One; snap back and surface the paywall if
  // a restored page sits beyond the free threshold.
  useEffect(() => {
    if (!entered || bookUnlocked || !book) return;
    const limit = book.free_page_limit || 24;
    if (currentPage > limit) {
      const lead = (spreads.find((s) => s.includes(limit)) || [limit])[0];
      setCurrentPage(lead);
      setPaywallOpen(true);
    }
  }, [entered, bookUnlocked, currentPage, book, spreads]);

  // (The "continue where you left off" button shows on page 1 and hides as
  // soon as the reader moves on — no auto-hide timer needed.)

  // Recurring Language Vault invitation — surfaces at each chapter as the reader moves through the book.
  const [vaultInvite, setVaultInvite] = useState(false);
  const inviteTimer = useRef(null);
  const lastInviteChapter = useRef(null);

  const inviteChapterId = useMemo(() => {
    const chs = book?.chapters || [];
    if (!chs.length) return null;
    let id = chs[0].id;
    for (const c of chs) if (currentPage >= c.start_page) id = c.id;
    return id;
  }, [book, currentPage]);

  useEffect(() => {
    if (!entered || !inviteChapterId) return;
    if (lastInviteChapter.current === inviteChapterId) return;
    lastInviteChapter.current = inviteChapterId;
    const showAt = setTimeout(() => {
      if (restPlateRef.current || pendingRestRef.current) return; // the "rest here" painting takes this moment
      setVaultInvite(true);
      if (inviteTimer.current) clearTimeout(inviteTimer.current);
      inviteTimer.current = setTimeout(() => setVaultInvite(false), 12000);
    }, 2400);
    return () => clearTimeout(showAt);
  }, [entered, inviteChapterId]);

  // "Rest here" — a gentle full-screen painting offered once when the reader
  // crosses into a new chapter. If a Waypoint is open, it waits until that
  // closes so the two never collide. Dismissible, never forced.
  const [restPlate, setRestPlate] = useState(null);
  const restPlateRef = useRef(null);
  const [galleryArt, setGalleryArt] = useState([]);
  const restPrevChapter = useRef(null);
  const restSeenChapters = useRef(new Set());
  const restHideTimer = useRef(null);
  const [pendingRest, setPendingRest] = useState(null);
  const pendingRestRef = useRef(null);

  useEffect(() => { restPlateRef.current = restPlate; }, [restPlate]);
  useEffect(() => { pendingRestRef.current = pendingRest; }, [pendingRest]);

  useEffect(() => {
    fetch(`${API}/gallery`).then((r) => r.json())
      .then((d) => setGalleryArt((d.items || []).filter((i) => i.kind === "art")))
      .catch(() => {});
  }, []);

  const dismissRest = useCallback(() => {
    if (restHideTimer.current) clearTimeout(restHideTimer.current);
    setRestPlate(null);
  }, []);

  // Queue a plate when the reader crosses into a new chapter — but keep it
  // RARE: at most twice per reading session, and spaced several chapters apart.
  const REST_MAX_PER_SESSION = 2;
  const restCrossings = useRef(0);
  useEffect(() => {
    if (!entered || !inviteChapterId || !galleryArt.length) return;
    if (restPrevChapter.current === null) { restPrevChapter.current = inviteChapterId; restSeenChapters.current.add(inviteChapterId); return; }
    if (inviteChapterId === restPrevChapter.current) return;
    restPrevChapter.current = inviteChapterId;
    if (restSeenChapters.current.has(inviteChapterId)) return;
    restSeenChapters.current.add(inviteChapterId);
    restCrossings.current += 1;
    const shown = Number(sessionStorage.getItem("bnm-rest-shown") || 0);
    if (shown >= REST_MAX_PER_SESSION) return;
    // Only on the 2nd crossing, then every 4th after — a gentle, occasional pause.
    if (!(restCrossings.current === 2 || restCrossings.current % 4 === 0)) return;
    sessionStorage.setItem("bnm-rest-shown", String(shown + 1));
    const chs = book?.chapters || [];
    const idx = Math.max(0, chs.findIndex((c) => c.id === inviteChapterId));
    setPendingRest(galleryArt[idx % galleryArt.length]);
  }, [entered, inviteChapterId, galleryArt, book]);

  // Reveal the queued plate once no full-screen overlay is open.
  useEffect(() => {
    if (!pendingRest || restPlate) return;
    if (waypointZone || pausePractice) return;
    const t = setTimeout(() => {
      setRestPlate(pendingRest);
      setPendingRest(null);
      if (vaultInvite) setVaultInvite(false);
      restHideTimer.current = setTimeout(() => setRestPlate(null), 11000);
    }, 700);
    return () => clearTimeout(t);
  }, [pendingRest, restPlate, waypointZone, pausePractice]);

  // End-of-book Reflection Journal invitation — surfaces once when the reader
  // reaches the final spread of the book (and no waypoint overlay is open).
  const [reflectInvite, setReflectInvite] = useState(false);
  const reflectInviteShown = useRef(false);
  useEffect(() => {
    if (!entered || !totalPages || reflectInviteShown.current) return;
    if (waypointZone || pausePractice) return;
    const atEnd = spreads.length ? spreads[spreads.length - 1].includes(currentPage) : currentPage >= totalPages;
    if (!atEnd) return;
    const t = setTimeout(() => {
      reflectInviteShown.current = true;
      setReflectInvite(true);
    }, 1600);
    return () => clearTimeout(t);
  }, [entered, totalPages, spreads, currentPage, waypointZone, pausePractice]);

  // --- Read aloud (ElevenLabs narration) ----------------------------------
  const [narrateEnabled, setNarrateEnabled] = useState(false);
  const [narrateState, setNarrateState] = useState("idle"); // idle | loading | playing
  const narrateAudioRef = useRef(null);
  const narrateUrlRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/narrate/status`)
      .then((r) => r.json())
      .then((d) => { if (alive) setNarrateEnabled(!!d.enabled); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const stopNarration = useCallback(() => {
    const a = narrateAudioRef.current;
    if (a) { a.pause(); a.src = ""; }
    if (narrateUrlRef.current) { URL.revokeObjectURL(narrateUrlRef.current); narrateUrlRef.current = null; }
    setNarrateState("idle");
  }, []);

  const toggleNarrate = useCallback(async () => {
    if (narrateState === "playing" || narrateState === "loading") { stopNarration(); return; }
    setNarrateState("loading");
    try {
      const res = await fetch(`${API}/narrate/page/${currentPage}`);
      if (!res.ok) throw new Error("narration unavailable");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      narrateUrlRef.current = url;
      let a = narrateAudioRef.current;
      if (!a) { a = new Audio(); narrateAudioRef.current = a; }
      a.src = url;
      a.onended = () => stopNarration();
      await a.play();
      setNarrateState("playing");
    } catch (e) {
      stopNarration();
    }
  }, [narrateState, currentPage, stopNarration]);

  // Stop narration whenever the page changes.
  useEffect(() => { stopNarration(); }, [currentPage, stopNarration]);

  const FREE_LIMIT = (book && book.free_page_limit) || 24;

  const goToSpread = useCallback(
    (idx, dir) => {
      if (idx < 0 || idx >= spreads.length) return;
      const leadPage = spreads[idx][0];
      // Chapter One is free; crossing the threshold opens the gentle paywall.
      if (!bookUnlocked && leadPage > ((book && book.free_page_limit) || 24)) {
        setPaywallOpen(true);
        return;
      }
      if (hapticsRef.current) hapticTick();
      setDirection(dir);
      setCurrentPage(leadPage);
    },
    [spreads, bookUnlocked, book]
  );

  const next = useCallback(() => goToSpread(spreadIndex + 1, 1), [goToSpread, spreadIndex]);
  const prev = useCallback(() => goToSpread(spreadIndex - 1, -1), [goToSpread, spreadIndex]);

  // --- Touch-first page turning -------------------------------------------
  // Finger swipe left = next page, right = previous. We only act on a clearly
  // horizontal, quick gesture so vertical scrolling of a zoomed page still
  // works. Edge resistance is inherent: goToSpread() no-ops past the covers.
  const SWIPE_DIST = 48; // px threshold
  const onReaderTouchStart = useCallback((e) => {
    if (e.touches.length !== 1) { touchStart.current = null; return; }
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  }, []);
  const onReaderTouchEnd = useCallback((e) => {
    const s = touchStart.current;
    touchStart.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    const dt = Date.now() - s.at;
    // horizontal intent only, reasonably quick
    if (Math.abs(dx) < SWIPE_DIST || Math.abs(dx) <= Math.abs(dy) * 1.3 || dt > 900) return;
    if (dx < 0) next();
    else prev();
  }, [next, prev]);

  const jumpToPage = useCallback(
    (p) => {
      const idx = spreads.findIndex((s) => s.includes(p));
      if (idx < 0) return;
      setDirection(spreads[idx][0] >= currentPage ? 1 : -1);
      setCurrentPage(spreads[idx][0]);
    },
    [spreads, currentPage]
  );

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!document.fullscreenElement) {
      el?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!entered) return;
    const onKey = (e) => {
      if (aboutOpen && e.key === "Escape") return setAboutOpen(false);
      if (ecoOpen && e.key === "Escape") return setEcoOpen(false);
      if (prefaceOpen && e.key === "Escape") return setPrefaceOpen(false);
      if (scrubOpen && e.key === "Escape") return setScrubOpen(false);
      // A pause practice overlay is open: any navigation/confirm key dismisses it,
      // and nothing turns the page behind it.
      if (pausePractice) {
        if (["Enter", " ", "Escape", "ArrowRight", "ArrowLeft", "PageDown", "PageUp"].includes(e.key)) {
          e.preventDefault();
          setPausePractice(null);
        }
        return;
      }
      // The Waypoint overlay is a gentle pause — don't let arrow keys silently
      // flip pages behind it. Continue/confirm keys dismiss it instead.
      if (waypointZone) {
        if (["Enter", " ", "Escape", "ArrowRight", "PageDown"].includes(e.key)) {
          e.preventDefault();
          setWaypointZone(null);
        }
        return;
      }
      if (["ArrowRight", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        next();
      } else if (["ArrowLeft", "PageUp"].includes(e.key)) {
        e.preventDefault();
        prev();
      } else if (e.key === "Home") {
        goToSpread(0, -1);
      } else if (e.key === "End") {
        goToSpread(spreads.length - 1, 1);
      } else if (e.key.toLowerCase() === "f") {
        toggleFullscreen();
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        zoomIn();
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        zoomOut();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entered, next, prev, goToSpread, spreads.length, toggleFullscreen, aboutOpen, ecoOpen, prefaceOpen, scrubOpen, waypointZone, pausePractice]);

  // Global Escape closes any open overlay (works before entering too)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (aboutOpen) setAboutOpen(false);
      if (ecoOpen) setEcoOpen(false);
      if (prefaceOpen) setPrefaceOpen(false);
      if (scrubOpen) setScrubOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aboutOpen, ecoOpen, prefaceOpen, scrubOpen]);

  // Idle-hide chrome for immersive reading
  const wakeChrome = useCallback(() => {
    setChromeVisible(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setChromeVisible(false), 3400);
  }, []);

  useEffect(() => {
    if (!entered) return;
    wakeChrome();
    const onMove = () => wakeChrome();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchstart", onMove);
    window.addEventListener("keydown", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchstart", onMove);
      window.removeEventListener("keydown", onMove);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [entered, wakeChrome]);

  const forceChrome = scrubOpen || aboutOpen || ecoOpen || prefaceOpen;
  const showChrome = chromeVisible || forceChrome;

  const cur = spreads[spreadIndex] || [1];
  // Free readers only have Chapter One loaded — clamp the label/progress to the
  // pages that actually exist so the last free spread reads as a single page.
  const effectiveMax = bookUnlocked ? pageCount : ((book && book.free_page_limit) || 24);
  const curVisible = cur.filter((p) => !effectiveMax || p <= effectiveMax);
  const labelPages = curVisible.length ? curVisible : [cur[0]];
  const pageLabel = labelPages.length === 2 ? `${labelPages[0]}–${labelPages[1]}` : `${labelPages[0]}`;
  const progress = effectiveMax ? Math.min(1, labelPages[labelPages.length - 1] / effectiveMax) : 0;

  return (
    <div
      ref={containerRef}
      className="relative h-[100dvh] w-full overflow-hidden watercolor-wash"
      data-testid="book-experience"
    >
      <SpatialTopography variant="reader" />

      {/* Header */}
      <AnimatePresence>
        {entered && (
          <motion.header
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: showChrome ? 1 : 0, y: showChrome ? 0 : -12 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-none fixed inset-x-0 top-0 z-30 flex items-center justify-between gap-2 px-4 py-3 safe-top safe-x sm:px-8 sm:py-4"
            style={{ pointerEvents: showChrome ? "auto" : "none" }}
            data-testid="reader-header"
          >
            <div className="min-w-0 leading-tight">
              <p className="truncate font-display text-base text-ink sm:text-2xl" data-testid="header-title">
                {book?.title || "Braced No More"}
              </p>
              <p className="hidden tracking-editorial text-[10px] uppercase text-ink-muted sm:block">
                {book?.author || "Casey Muze"}
              </p>
            </div>
            <div className="flex flex-none items-center gap-1 sm:gap-2">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                data-testid="search-open-btn"
                title="Search inside"
                aria-label="Search inside the book"
                className="touch-target inline-flex items-center gap-1.5 rounded-full border border-ivory-400/70 px-2.5 py-1.5 font-display text-xs text-ink-soft transition hover:border-gold/50 hover:text-gold-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:px-4 sm:py-2 sm:text-base"
              >
                <Search strokeWidth={1.6} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Search</span>
              </button>
              <button
                type="button"
                onClick={() => setTrailOpen(true)}
                data-testid="your-trail-btn"
                title="Your Trail"
                className="touch-target inline-flex items-center gap-1.5 rounded-full border border-ivory-400/70 px-2.5 py-1.5 font-display text-xs text-ink-soft transition hover:border-gold/50 hover:text-gold-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:px-4 sm:py-2 sm:text-base"
              >
                <Compass strokeWidth={1.6} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Your Trail</span>
              </button>
              <Link
                to="/vault"
                data-testid="header-vault-link"
                title="The Language Vault"
                className="vault-cta touch-target inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 font-display text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:px-4 sm:py-2 sm:text-base"
              >
                <BookLock strokeWidth={1.6} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="sm:hidden">Vault</span>
                <span className="hidden sm:inline">The Language Vault</span>
              </Link>
              <button
                type="button"
                onClick={() => setPrefaceOpen(true)}
                data-testid="open-preface-btn"
                className="debossed-btn hidden rounded-full px-2.5 py-1.5 font-display text-xs text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:inline-flex sm:px-4 sm:py-2 sm:text-base"
              >
                Preface
              </button>
              {bookExtras.flags?.trail_map && (
                <button
                  type="button"
                  onClick={() => setTrailMapOpen(true)}
                  data-testid="open-trail-map-btn"
                  className="debossed-btn hidden rounded-full px-2.5 py-1.5 font-display text-xs text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:inline-flex sm:px-4 sm:py-2 sm:text-base"
                >
                  <Map strokeWidth={1.6} className="mr-1.5 h-4 w-4 text-gold-deep" /> Trail Map
                </button>
              )}
              <button
                type="button"
                onClick={() => setEcoOpen(true)}
                data-testid="open-ecosystem-btn"
                className="debossed-btn hidden rounded-full px-2.5 py-1.5 font-display text-xs text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:inline-flex sm:px-4 sm:py-2 sm:text-base"
              >
                <span className="sm:hidden">Ecosystem</span>
                <span className="hidden sm:inline">The Ecosystem</span>
              </button>
              <Link
                to="/vault/gallery"
                data-testid="header-gallery-link"
                className="debossed-btn hidden rounded-full px-2.5 py-1.5 font-display text-xs text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:inline-flex sm:px-4 sm:py-2 sm:text-base"
              >
                Gallery
              </Link>
              <button
                type="button"
                onClick={() => setAboutOpen(true)}
                data-testid="open-about-btn"
                className="debossed-btn hidden rounded-full px-2.5 py-1.5 font-display text-xs text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:inline-flex sm:px-4 sm:py-2 sm:text-base"
              >
                About
              </button>

              {/* Mobile overflow menu — keeps the header within the viewport */}
              <div className="relative sm:hidden">
                <button
                  type="button"
                  onClick={() => setMoreOpen((v) => !v)}
                  data-testid="header-more-btn"
                  aria-label="More options"
                  aria-expanded={moreOpen}
                  className="debossed-btn touch-target rounded-full px-2.5 font-display text-xs text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <MoreHorizontal strokeWidth={1.8} className="h-5 w-5" />
                </button>
                <AnimatePresence>
                  {moreOpen && (
                    <>
                      <div className="fixed inset-0 z-[44]" onClick={() => setMoreOpen(false)} data-testid="more-menu-overlay" />
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute right-0 top-full z-[46] mt-2 w-48 overflow-hidden rounded-xl bg-ivory-50 p-1.5 shadow-xl ring-1 ring-ivory-400/60"
                        data-testid="more-menu"
                      >
                        <button type="button" onClick={() => { setPrefaceOpen(true); setMoreOpen(false); }} data-testid="more-preface" className="flex w-full items-center rounded-lg px-3 py-2.5 text-left font-display text-sm text-ink hover:bg-gold/10">Preface</button>
                        {bookExtras.flags?.trail_map && (
                          <button type="button" onClick={() => { setTrailMapOpen(true); setMoreOpen(false); }} data-testid="more-trail-map" className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left font-display text-sm text-ink hover:bg-gold/10"><Map className="h-4 w-4 text-gold-deep" /> Trail Map</button>
                        )}
                        <button type="button" onClick={() => { setEcoOpen(true); setMoreOpen(false); }} data-testid="more-ecosystem" className="flex w-full items-center rounded-lg px-3 py-2.5 text-left font-display text-sm text-ink hover:bg-gold/10">The Ecosystem</button>
                        <Link to="/vault/gallery" onClick={() => setMoreOpen(false)} data-testid="more-gallery" className="flex w-full items-center rounded-lg px-3 py-2.5 text-left font-display text-sm text-ink hover:bg-gold/10">Art Gallery</Link>
                        <button type="button" onClick={() => { setAboutOpen(true); setMoreOpen(false); }} data-testid="more-about" className="flex w-full items-center rounded-lg px-3 py-2.5 text-left font-display text-sm text-ink hover:bg-gold/10">About</button>

                        <div className="my-1.5 h-px bg-ivory-400/60" />
                        <button type="button" onClick={() => setHapticsOn((v) => !v)} data-testid="more-haptics-toggle" aria-pressed={hapticsOn} className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left font-display text-sm text-ink hover:bg-gold/10">
                          <span className="inline-flex items-center gap-2"><Vibrate className="h-4 w-4 text-ink-soft" /> Page-turn haptics</span>
                          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${hapticsOn ? "border-gold bg-gold/15 text-gold-deep" : "border-ivory-400 text-transparent"}`}>{hapticsOn && <Check className="h-3.5 w-3.5" />}</span>
                        </button>
                        <button type="button" onClick={() => setPortraitLockState((v) => !v)} data-testid="more-portrait-toggle" aria-pressed={portraitLock} className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left font-display text-sm text-ink hover:bg-gold/10">
                          <span className="inline-flex items-center gap-2"><RotateCcw className="h-4 w-4 text-ink-soft" /> Lock to portrait</span>
                          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${portraitLock ? "border-gold bg-gold/15 text-gold-deep" : "border-ivory-400 text-transparent"}`}>{portraitLock && <Check className="h-3.5 w-3.5" />}</span>
                        </button>
                        <div className="my-1.5 h-px bg-ivory-400/60" />
                        <Link to="/privacy" onClick={() => setMoreOpen(false)} data-testid="more-privacy" className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left font-display text-sm text-ink hover:bg-gold/10"><Shield className="h-4 w-4 text-ink-soft" /> Privacy policy</Link>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      {/* Gentle gallery invite for book owners */}
      <AnimatePresence>
        {galleryNudge && !restPlate && !paywallOpen && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-[42] w-[calc(100%-2rem)] max-w-sm sm:bottom-6 sm:right-6 sm:w-auto safe-x"
            data-testid="gallery-nudge"
          >
            <div className="relative overflow-hidden rounded-2xl bg-ivory-50 p-5 shadow-[0_24px_60px_-30px_rgba(60,46,30,0.55)] ring-1 ring-gold/30">
              <button
                type="button"
                onClick={dismissGalleryNudge}
                data-testid="gallery-nudge-dismiss"
                aria-label="Dismiss"
                className="absolute right-3 top-3 rounded-full p-1.5 text-ink-muted transition hover:bg-gold/10 hover:text-ink"
              >
                <X strokeWidth={1.8} className="h-4 w-4" />
              </button>
              <div className="flex items-start gap-3 pr-6">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-gold/10 ring-1 ring-gold/40">
                  <Images strokeWidth={1.4} className="h-5 w-5 text-gold-deep" />
                </span>
                <div className="min-w-0">
                  <p className="kicker text-gold-foil">A room beyond the pages</p>
                  <p className="mt-1 font-display text-lg leading-tight text-ink">Step into the gallery</p>
                  <p className="mt-1 text-sm leading-relaxed text-ink-soft">
                    The paintings and words of this book wait as a quiet exhibition. Wander it whenever you need to breathe.
                  </p>
                  <Link
                    to="/vault/gallery"
                    onClick={dismissGalleryNudge}
                    data-testid="gallery-nudge-cta"
                    className="debossed-btn mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2 font-display text-sm text-ink transition hover:gap-3"
                  >
                    <Sparkles strokeWidth={1.6} className="h-4 w-4 text-gold-deep" /> Step into the gallery
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Reader stage */}
      <div
        className="reader-surface absolute inset-0 flex items-center justify-center px-2 py-14 sm:px-4"
        onTouchStart={onReaderTouchStart}
        onTouchEnd={onReaderTouchEnd}
        data-testid="reader-swipe-surface"
      >
        {book ? (
          <FlipbookReader
            spreads={spreads}
            spreadIndex={spreadIndex}
            direction={direction}
            isMobile={isMobile}
            zoom={zoom}
            bookmarkPage={bookmarkPage}
            pdfUrl={pdfUrl}
          />
        ) : (
          <LoadingContour label="Opening the book" />
        )}
      </div>

      {entered && !paywallOpen && <ReaderCompanion chapterTitle={book?.title} />}

      {/* Controls */}
      {entered && (
        <ReaderControls
          visible={showChrome}
          onPrev={prev}
          onNext={next}
          canPrev={spreadIndex > 0}
          canNext={spreadIndex < spreads.length - 1}
          pageLabel={pageLabel}
          progress={progress}
          onToggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
          onOpenScrub={() => setScrubOpen(true)}
          musicOn={musicOn}
          onToggleMusic={toggleMusic}
          onOpenMusicPanel={() => setMusicPanelOpen((v) => !v)}
          musicPanelOpen={musicPanelOpen}
          volume={volume}
          onVolumeChange={onVolumeChange}
          zoom={zoom}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          canZoomIn={zoom < ZOOM_MAX}
          canZoomOut={zoom > ZOOM_MIN}
          narrateEnabled={narrateEnabled}
          narrateState={narrateState}
          onToggleNarrate={toggleNarrate}
        />
      )}

      <audio ref={deckARef} loop preload="auto" data-testid="chapter-audio-a" />
      <audio ref={deckBRef} loop preload="auto" data-testid="chapter-audio-b" />

      {/* Quick-recall music panel */}
      <AnimatePresence>
        {entered && musicPanelOpen && (
          <>
            <div
              className="fixed inset-0 z-[45]"
              onClick={() => setMusicPanelOpen(false)}
              data-testid="music-panel-overlay"
            />
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="veil-plate fixed bottom-24 left-1/2 z-[46] w-[min(92vw,360px)] -translate-x-1/2 rounded-2xl p-3"
              data-testid="music-panel"
            >
              <div className="mb-2 flex items-center justify-between px-1">
                <p className="kicker text-ink-muted">Soundtrack</p>
                <button
                  type="button"
                  onClick={enableAutoFollow}
                  data-testid="music-follow-btn"
                  className={`rounded-full px-2.5 py-1 text-[11px] tracking-editorial transition ${
                    autoFollow ? "bg-gold/20 text-gold-deep" : "text-ink-muted hover:text-gold-deep"
                  }`}
                  title="Let the music follow the book"
                >
                  Follow the book
                </button>
              </div>
              <div className="max-h-[46vh] space-y-1 overflow-y-auto pr-0.5" data-testid="music-track-list">
                {(book?.tracks || []).map((t, i) => {
                  const isActive = t.id === activeTrackId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => selectTrack(t.id)}
                      data-testid={`track-item-${t.id}`}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                        isActive ? "bg-gold/15 ring-1 ring-gold/40" : "hover:bg-ink/[0.05]"
                      }`}
                    >
                      <span
                        className={`flex h-7 w-7 flex-none items-center justify-center rounded-full font-display text-[13px] num-old ${
                          isActive ? "bg-gold text-ivory" : "bg-ivory-400/50 text-ink-soft"
                        }`}
                      >
                        {isActive && musicOn ? (
                          <Volume2 strokeWidth={1.6} className="h-3.5 w-3.5" />
                        ) : (
                          i + 1
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-display text-[15px] text-ink">{t.title}</span>
                        {t.opener && (
                          <span className="block text-[11px] tracking-editorial text-ink-muted">
                            Plays on opening
                          </span>
                        )}
                      </span>
                      {isActive && (
                        <span className="flex-none text-[10px] uppercase tracking-editorial text-gold-deep">
                          {musicOn ? "Now playing" : "Selected"}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* "Rest here" — gentle painting between chapters */}
      <AnimatePresence>
        {entered && restPlate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-50 flex items-center justify-center p-5 safe-top safe-bottom"
            style={{ background: "rgba(30,34,41,0.90)", backdropFilter: "blur(8px)" }}
            onClick={dismissRest}
            data-testid="rest-here"
          >
            <button
              type="button"
              onClick={dismissRest}
              aria-label="Keep reading"
              data-testid="rest-here-close"
              className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white/70 transition hover:text-white"
            >
              <X strokeWidth={1.6} className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.99 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="flex w-full max-w-2xl flex-col items-center text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="kicker mb-4 text-white/50">Rest here a moment</p>
              <img src={restPlate.image} alt={restPlate.title} className="max-h-[56vh] w-auto rounded-2xl object-contain shadow-2xl" />
              <p className="mt-5 font-display text-2xl text-white">{restPlate.title}</p>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-white/70">{restPlate.caption}</p>
              <button
                type="button"
                onClick={dismissRest}
                data-testid="rest-here-continue"
                className="mt-6 rounded-full border border-white/25 px-6 py-2.5 font-display text-sm text-white/90 transition hover:bg-white/10"
              >
                Keep reading when you're ready
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* End-of-book Reflection Journal invitation */}
      <AnimatePresence>
        {entered && reflectInvite && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-50 flex items-center justify-center px-5 safe-top safe-bottom"
            style={{ background: "rgba(42,38,34,0.42)", backdropFilter: "blur(10px)" }}
            data-testid="reflect-invite"
          >
            <motion.div
              initial={{ opacity: 0, y: 28, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              className="veil-plate relative w-[min(94vw,460px)] rounded-3xl p-8 text-center"
            >
              <button
                type="button"
                onClick={() => setReflectInvite(false)}
                data-testid="reflect-invite-dismiss"
                aria-label="Not now"
                className="absolute right-4 top-4 text-ink-muted transition hover:text-ink"
              >
                <X strokeWidth={1.6} className="h-4 w-4" />
              </button>
              <p className="kicker text-gold-deep">A Quiet Inventory</p>
              <h3 className="mt-2 font-display text-3xl leading-tight text-ink">
                Set it down, one breath at a time.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-soft">
                You've reached the last page. Before you close the book, you're invited to a few
                gentle prompts — a place to notice what you're carrying, and set a little of it down.
              </p>
              <Link
                to="/reflect"
                data-testid="reflect-invite-link"
                className="vault-cta mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm sm:text-base"
              >
                Begin the reflection
                <ArrowUpRight strokeWidth={1.8} className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={() => setReflectInvite(false)}
                data-testid="reflect-invite-later"
                className="mt-3 block w-full text-xs text-ink-muted transition hover:text-ink"
              >
                Maybe later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recurring Language Vault invitation */}
      <AnimatePresence>
        {entered && vaultInvite && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-40 w-[min(92vw,340px)] sm:right-6"
            data-testid="vault-invite"
          >
            <div className="veil-plate relative rounded-2xl p-5">
              <button
                type="button"
                onClick={() => setVaultInvite(false)}
                data-testid="vault-invite-dismiss"
                aria-label="Dismiss invitation"
                className="absolute right-3 top-3 text-ink-muted transition hover:text-ink"
              >
                <X strokeWidth={1.6} className="h-4 w-4" />
              </button>
              <p className="kicker text-gold-deep">An invitation</p>
              <h3 className="mt-1.5 font-display text-2xl leading-tight text-ink">The Language Vault</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                Some words dress themselves up as identity while quietly locking the door from the
                inside. Step into a transparent space to sort the language tied to your emotions —
                and decide what still deserves a home inside you.
              </p>
              <Link
                to="/vault"
                data-testid="vault-invite-link"
                className="vault-cta mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-sm sm:text-base"
              >
                Enter the Vault
                <ArrowUpRight strokeWidth={1.8} className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Continue-where-you-left-off button — only on page 1 for returning readers */}
      <AnimatePresence>
        {entered && currentPage === 1 && resumePage > 1 && (
          <motion.button
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => jumpToPage(resumePage)}
            className="fixed left-1/2 top-20 z-30 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-ink/85 px-5 py-2.5 text-center shadow-lg backdrop-blur-md transition hover:bg-ink"
            data-testid="continue-reading-btn"
          >
            <RotateCcw className="h-4 w-4 text-gold-light" />
            <span className="font-display text-xs tracking-editorial text-ivory">
              Continue where you left off · page {resumePage}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <YourTrail
        open={trailOpen}
        onClose={() => setTrailOpen(false)}
        reader={reader}
        config={trailConfig}
        currentPage={currentPage}
        onJump={trailJump}
        flags={bookExtras.flags}
        sessionPrefill={sessionPrefill}
        onSessionPrefillUsed={() => setSessionPrefill(false)}
      />
      <SearchInside
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onJump={(p) => { setSearchOpen(false); jumpToPage(p); }}
      />
      <Waypoint
        zone={waypointZone}
        prevGap={waypointZone ? (ZONES[Math.max(0, ZONES.findIndex((z) => z.id === waypointZone.id) - 1)] || {}).gap : 6}
        reducedMotion={reader.state?.settings?.reduced_motion}
        onClose={() => setWaypointZone(null)}
        onJournal={() => { setWaypointZone(null); setTrailOpen(true); }}
        onPause={() => {
          const pauses = trailConfig?.pauses || [];
          if (pauses.length) setPausePractice(pauses[Math.floor(Math.random() * pauses.length)]);
          setWaypointZone(null);
        }}
        showBringToSession={bookExtras.flags?.bring_to_session}
        onBringToSession={() => { setWaypointZone(null); setSessionPrefill(true); setTrailOpen(true); }}
      />
      <PausePractice pause={pausePractice} onClose={() => setPausePractice(null)} />

      <TrailMap
        open={trailMapOpen}
        onClose={() => setTrailMapOpen(false)}
        currentPage={currentPage}
        onJump={(p) => { if (!entered) { beginMusic(); setEntered(true); } jumpToPage(p); }}
        unlocked={bookUnlocked}
        freeLimit={(book && book.free_page_limit) || 24}
        note={bookExtras.trail_map_note}
        pauses={trailConfig?.pauses || []}
        socialWaypoints={bookExtras.social_waypoints || []}
      />
      {bookExtras.flags?.plain_words && reader.state?.settings?.plain_words && (() => {
        const card = (bookExtras.plain_words || []).find((c) => c.page === currentPage || c.page === currentPage + 1);
        return card ? <PlainWordsCard card={card} /> : null;
      })()}

      {/* Return greeting */}
      <AnimatePresence>
        {entered && greeting && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="pointer-events-none fixed left-1/2 top-9 z-40 -translate-x-1/2 rounded-full bg-ink/80 px-5 py-2.5 backdrop-blur-md"
            data-testid="return-greeting"
          >
            <span className="font-display text-sm tracking-editorial text-ivory">{greeting}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Now-playing track indicator */}
      <AnimatePresence>
        {entered && musicOn && trackToast && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-none fixed bottom-24 left-1/2 z-30 -translate-x-1/2 rounded-full bg-ink/80 px-4 py-2 text-center backdrop-blur-md"
            data-testid="track-toast"
          >
            <span className="flex items-center gap-2 font-display text-xs tracking-editorial text-ivory">
              <Music2 strokeWidth={1.4} className="h-3.5 w-3.5 text-gold-foil" />
              {trackToast}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <ThumbnailScrub
        open={scrubOpen}
        onClose={() => setScrubOpen(false)}
        pageCount={pageCount}
        currentPage={cur[0]}
        onJump={(p) => {
          jumpToPage(p);
        }}
      />

      <AboutAuthor
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        book={book}
        onOpenEcosystem={() => {
          setAboutOpen(false);
          setEcoOpen(true);
        }}
      />

      <Ecosystem open={ecoOpen} onClose={() => setEcoOpen(false)} book={book} />

      <Preface
        open={prefaceOpen}
        onClose={() => setPrefaceOpen(false)}
        book={book}
        onEnterBook={() => {
          setPrefaceOpen(false);
          if (returning) { beginMusic(); setEntered(true); }
        }}
      />

      {/* The Threshold of the Exhale — gentle paywall at the end of Chapter One */}
      <AnimatePresence>
        {entered && paywallOpen && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto watercolor-veil"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            data-testid="paywall-threshold"
          >
            <div className="veil-scrim pointer-events-none absolute inset-0 z-[5]" />
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="veil-plate relative z-10 mx-auto my-8 w-[92vw] max-w-2xl rounded-[24px] px-7 py-10 sm:px-10"
            >
              <button
                type="button"
                onClick={() => setPaywallOpen(false)}
                data-testid="paywall-close"
                aria-label="Return to Chapter One"
                className="absolute right-4 top-4 rounded-full p-2 text-ink-muted transition hover:text-ink"
              >
                <X strokeWidth={1.5} className="h-5 w-5" />
              </button>
              <div className="gold-rule mx-auto mb-6 w-16" />
              <p className="kicker text-center text-gold-foil">The first door</p>
              <h2 className="mt-3 text-center font-display text-3xl text-ink sm:text-4xl">
                The Threshold of the Exhale
              </h2>
              <p className="deck mx-auto mt-4 max-w-md text-center text-lg leading-relaxed text-ink-soft">
                You have carried the narrative of survival for long enough. You do not have to hold the rest alone.
              </p>
              <p className="mt-5 text-[15px] italic leading-relaxed text-ink-soft">
                "You have met the first door. What follows is not another manual of expectations, but a systematic,
                biopsychosocial map for setting down the quiet endurance your body has held for years. To honor the
                weight and preservation of this work, the complete volume of Braced No More is available for you to
                carry forward."
              </p>

              {pwLeadSaved ? (
                <div className="mt-6 rounded-2xl bg-sage/10 px-5 py-4 text-center ring-1 ring-sage/30" data-testid="paywall-lead-saved">
                  <p className="font-display text-ink">Thank you, {pwName.trim().split(" ")[0] || "friend"} — Casey has your details.</p>
                  <p className="mt-1 text-sm text-ink-soft">Whenever you're ready, the full book waits just below.</p>
                </div>
              ) : (
                <form onSubmit={submitPaywallLead} className="mt-6 rounded-2xl bg-ivory-50/70 p-5 ring-1 ring-ivory-400/60" data-testid="paywall-lead-form">
                  <p className="kicker text-ink-muted">Stay close to the work</p>
                  <p className="mt-1 text-sm text-ink-soft">Leave your details and Casey will keep you gently in the loop — no pressure, unsubscribe anytime.</p>
                  <div className="mt-3 grid gap-2.5 sm:grid-cols-3">
                    <input value={pwName} onChange={(e) => setPwName(e.target.value)} placeholder="First name" data-testid="paywall-lead-name"
                      className="rounded-lg border border-ivory-400/70 bg-[#FCFBF7] px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                    <input type="email" value={pwEmail} onChange={(e) => setPwEmail(e.target.value)} placeholder="Email" data-testid="paywall-lead-email"
                      className="rounded-lg border border-ivory-400/70 bg-[#FCFBF7] px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                    <input type="tel" value={pwPhone} onChange={(e) => setPwPhone(e.target.value)} placeholder="Phone (optional)" data-testid="paywall-lead-phone"
                      className="rounded-lg border border-ivory-400/70 bg-[#FCFBF7] px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                  </div>
                  {pwLeadErr && <p className="mt-2 text-sm text-terracotta" data-testid="paywall-lead-error">{pwLeadErr}</p>}
                  <button type="submit" disabled={pwLeadSaving} data-testid="paywall-lead-submit"
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 disabled:opacity-50">
                    {pwLeadSaving ? "Saving…" : "Keep me close"}
                  </button>
                </form>
              )}

              <div className="mt-7 -mx-1 flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto px-1 pb-2" data-testid="paywall-offers">
                <Link
                  to="/checkout?product=braced-no-more-book"
                  data-testid="paywall-buy-book"
                  className="group flex w-[78vw] max-w-[320px] flex-none snap-start flex-col overflow-hidden rounded-2xl bg-ivory-50 text-left shadow-sm ring-1 ring-ivory-400/60 transition hover:-translate-y-0.5 hover:shadow-md hover:ring-gold/50 sm:w-0 sm:max-w-none sm:flex-1"
                >
                  <img src="/offerings/braced-no-more-book.jpg" alt="" aria-hidden="true" loading="lazy" className="h-24 w-full object-cover transition-transform duration-[650ms] ease-out group-hover:scale-[1.08] sm:h-28" />
                  <div className="flex flex-1 flex-col p-5">
                    <span className="font-display text-lg text-ink">Complete Volume — $33.99</span>
                    <span className="mt-1 flex-1 text-sm leading-relaxed text-ink-muted">One-time. Full lifetime digital access to all chapters, somatic pauses, and companion materials.</span>
                    <span className="mt-3 inline-flex items-center gap-1 font-display text-sm text-gold-deep transition group-hover:gap-2">Carry it forward →</span>
                  </div>
                </Link>

                <Link
                  to="/checkout?product=braced-no-more-book&bump=1"
                  data-testid="paywall-buy-bundle"
                  className="group flex w-[78vw] max-w-[320px] flex-none snap-start flex-col overflow-hidden rounded-2xl border-2 border-dashed border-sage/50 bg-sage/10 text-left transition hover:-translate-y-0.5 hover:border-sage sm:w-0 sm:max-w-none sm:flex-1"
                >
                  <img src="/offerings/the-language-vault.jpg" alt="" aria-hidden="true" loading="lazy" className="h-24 w-full object-cover transition-transform duration-[650ms] ease-out group-hover:scale-[1.08] sm:h-28" />
                  <div className="flex flex-1 flex-col p-5">
                    <span className="font-display text-lg text-ink">Integrated Sanctuary — Book + Vault</span>
                    <span className="mt-1 flex-1 text-sm leading-relaxed text-ink-soft">The complete book ($33.99) with The Language Vault — $15 first month, then $25/mo · cancel anytime.</span>
                    <span className="mt-3 inline-flex items-center gap-1 font-display text-sm text-sage-deep transition group-hover:gap-2">Add the Vault →</span>
                  </div>
                </Link>
              </div>
              <p className="mt-1 text-center text-[11px] text-ink-muted sm:hidden">Swipe to compare both →</p>

              <form onSubmit={submitAccessCode} className="mt-7 flex flex-col items-center gap-2.5" data-testid="paywall-code-form">
                <label htmlFor="paywall-code" className="kicker text-ink-muted">
                  Have an access code from Casey?
                </label>
                <input
                  id="paywall-code"
                  value={codeInput}
                  onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); if (codeError) setCodeError(""); }}
                  placeholder="Enter your access code"
                  autoCapitalize="characters"
                  spellCheck={false}
                  data-testid="paywall-code-input"
                  className="w-full max-w-[340px] rounded-full border border-white/60 bg-ivory/70 px-5 py-3 text-center font-display tracking-editorial text-ink placeholder:text-ink-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                />
                <button
                  type="submit"
                  disabled={!codeInput.trim() || codeSubmitting}
                  data-testid="paywall-code-submit"
                  className="debossed-btn rounded-full px-7 py-2.5 font-display text-ink transition disabled:opacity-40"
                >
                  {codeSubmitting ? "Opening…" : "Unlock the full volume"}
                </button>
                {codeError && <p className="text-sm text-terracotta-deep" data-testid="paywall-code-error">{codeError}</p>}
              </form>

              <div className="my-7 h-px w-full bg-ivory-400/50" />
              <ArtGalleryTeaser variant="chapter1" className="w-full" />


              <button
                type="button"
                onClick={() => setPaywallOpen(false)}
                data-testid="paywall-stay-free"
                className="mx-auto mt-5 block text-xs text-ink-muted underline underline-offset-4 transition hover:text-gold-deep"
              >
                Stay with Chapter One for now
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Entry veil */}
      <AnimatePresence>
        {!entered && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto watercolor-veil safe-top safe-bottom safe-x"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            data-testid="entry-veil"
          >
            <SpatialTopography variant="veil" className="hero-bg-hide" />
            <div className="veil-scrim hero-bg-hide pointer-events-none absolute inset-0 z-[5]" />

            {/* Ambient sound — a quiet icon-only toggle in the top-right corner */}
            <div className="safe-top safe-x fixed right-3 top-3 z-[80] sm:right-5 sm:top-5">
              <button
                type="button"
                onClick={toggleMusic}
                data-testid="veil-music-btn"
                aria-pressed={musicOn}
                aria-label={musicOn ? "Turn off ambient music" : "Play ambient music"}
                title={musicOn ? "Ambient music: on" : "Play ambient music"}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/60 bg-ivory/70 text-ink-soft shadow-sm backdrop-blur-sm transition hover:border-gold/50 hover:text-gold-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
              >
                {musicOn ? (
                  <Volume2 strokeWidth={1.5} className="h-[18px] w-[18px] text-gold-deep" />
                ) : (
                  <Music2 strokeWidth={1.5} className="h-[18px] w-[18px]" />
                )}
              </button>
            </div>

            <div className="relative z-10 my-10 w-full">
              {/* HERO — robust two-column grid */}
              <section className="hero-grid veil-plate rounded-[24px]" data-testid="hero">
                {/* Left — the cover */}
                <div className="hero-cover flex flex-col items-center">
                  {APPS_LIVE && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      data-testid="launch-ribbon"
                      className="mb-5 inline-flex items-center gap-2 rounded-full bg-gold/15 px-4 py-1.5 font-display text-xs tracking-[0.12em] text-gold-deep ring-1 ring-gold/30"
                    >
                      <Sparkles strokeWidth={1.6} className="h-3.5 w-3.5" />
                      Now on iOS &amp; Android
                    </motion.div>
                  )}
                  <motion.div
                    initial={{ opacity: 0, y: 44, rotateY: -16, scale: 0.92 }}
                    animate={{ opacity: 1, y: 0, rotateY: 0, scale: 1 }}
                    transition={{ duration: 1.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    style={{ transformPerspective: 1300 }}
                    className="hero-cover-inner relative"
                  >
                    {/* Breathing gold halo behind the cover */}
                    <motion.div
                      aria-hidden
                      className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[118%] w-[118%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-2xl"
                      style={{ background: "radial-gradient(ellipse at center, rgba(198,164,92,0.34) 0%, rgba(198,164,92,0.12) 45%, transparent 72%)" }}
                      animate={prefersReduced ? {} : { opacity: [0.4, 0.72, 0.4], scale: [0.94, 1.06, 0.94] }}
                      transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
                    />
                    {/* Gently floating cover with a passing foil sheen */}
                    <motion.div
                      animate={prefersReduced ? {} : { y: [0, -11, 0], rotateZ: [0, 0.7, 0] }}
                      transition={{ duration: 7.5, repeat: Infinity, ease: "easeInOut" }}
                      className="book-shadow relative z-10 overflow-hidden rounded-[10px] ring-1 ring-white/50"
                    >
                      <img
                        src={COVER_IMG}
                        alt={`${book?.title || "Braced No More"} — cover`}
                        className="hero-cover-img"
                        draggable={false}
                      />
                      <motion.div
                        aria-hidden
                        className="pointer-events-none absolute inset-0"
                        style={{ background: "linear-gradient(105deg, transparent 32%, rgba(198,164,92,0.22) 46%, rgba(255,250,236,0.5) 50%, rgba(198,164,92,0.22) 54%, transparent 68%)" }}
                        initial={{ x: "-130%" }}
                        animate={prefersReduced ? {} : { x: ["-130%", "150%"] }}
                        transition={{ duration: 5, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" }}
                      />
                    </motion.div>
                  </motion.div>
                </div>

                {/* Right — the invitation */}
                <div className="hero-text">
                  <div className="gold-rule mb-5 w-16" />
                  <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="kicker hero-eyebrow mb-4 text-gold-foil"
                  >
                    {site?.hero?.eyebrow || "A Virtual Reading"}
                  </motion.p>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.6 }}
                    className="deck hero-headline"
                  >
                    {site?.hero?.subtitle || book?.cover_line ||
                      "A close look at how the nervous system adapts under strain — and how it can learn something different."}
                  </motion.p>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.7 }}
                    className="mt-3 font-display text-sm text-ink-soft"
                    data-testid="entry-free-line"
                  >
                    Chapter One is free to read.
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="hero-ctas-wrap mt-8 flex w-full flex-col gap-4"
                  >
                    <div className="flex w-full max-w-[320px] flex-col items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { beginMusic(); setEntered(true); }}
                        disabled={!book}
                        data-testid="begin-reading-btn"
                        className="debossed-btn hero-btn w-full rounded-full font-display text-xl text-ink transition disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                      >
                        {returning && readerName ? `Continue reading, ${readerName}` : "Begin Reading"}
                      </button>
                      {bookExtras.flags?.trail_map && (
                        <button
                          type="button"
                          onClick={() => setTrailMapOpen(true)}
                          data-testid="veil-trail-map"
                          className="inline-flex items-center gap-1.5 text-sm text-gold-deep underline-offset-4 transition hover:gap-2.5 hover:underline"
                        >
                          <Map strokeWidth={1.6} className="h-4 w-4" /> See the Trail Map first
                        </button>
                      )}
                      <Link
                        to="/somatic-reset"
                        data-testid="veil-somatic-reset"
                        className="inline-flex items-center gap-1.5 text-sm text-gold-deep underline-offset-4 transition hover:gap-2.5 hover:underline"
                      >
                        <Wind strokeWidth={1.6} className="h-4 w-4" /> Free: the 1-Minute Somatic Reset
                      </Link>
                    </div>

                    <div className="w-full max-w-[320px]">
                      {!restoreMode ? (
                        <button
                          type="button"
                          onClick={() => setRestoreMode(true)}
                          data-testid="continue-trail-toggle"
                          className="text-xs text-ink-muted underline underline-offset-4 transition hover:text-gold-deep"
                        >
                          Continue my trail from another device
                        </button>
                      ) : (
                        <form onSubmit={submitRestore} className="flex flex-col items-center gap-2" data-testid="continue-trail-form">
                          <label htmlFor="trail-code" className="kicker text-ink-muted">Enter your trail code</label>
                          <input
                            id="trail-code"
                            value={restoreCode}
                            onChange={(e) => { setRestoreCode(e.target.value.toUpperCase()); if (restoreError) setRestoreError(""); }}
                            placeholder="RIDGE-CEDAR-4182"
                            data-testid="trail-code-input"
                            autoCapitalize="characters"
                            className="w-full rounded-full border border-white/60 bg-ivory/70 px-5 py-3 text-center font-display tracking-[0.18em] text-ink placeholder:text-ink-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                          />
                          <button type="submit" disabled={!restoreCode.trim()} data-testid="trail-code-submit" className="debossed-btn rounded-full px-7 py-2.5 font-display text-ink disabled:opacity-40">Restore my trail</button>
                          {restoreError && <p className="text-sm text-terracotta-deep" data-testid="trail-code-error">{restoreError}</p>}
                        </form>
                      )}
                    </div>

                    {book?.links?.braced_call && (
                      <a
                        href={book.links.braced_call}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="veil-braced-call"
                        className="hero-btn rounded-full border border-gold/50 font-display text-sm text-gold-deep transition hover:border-gold hover:bg-gold/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                      >
                        <Phone strokeWidth={1.5} className="mr-2 h-4 w-4" />
                        Book a free Braced Call
                      </a>
                    )}
                  </motion.div>
                </div>
              </section>

              {/* Below the hero — gallery teaser + offerings, centered */}
              <div className="mx-auto mt-10 w-full max-w-md px-[clamp(16px,4vw,48px)]">
                <ArtGalleryTeaser variant="home" className="w-full" />
              </div>

              <div className="mx-auto mt-10 w-full max-w-4xl px-[clamp(16px,4vw,48px)] text-center" data-testid="veil-offerings">
                <p className="kicker mb-1 text-ink-muted">The deeper work</p>
                <p className="mb-5 font-display text-2xl text-ink">When you're ready, there's more.</p>
                <OfferingsStrip />
              </div>

              <div className="mx-auto mt-12 w-full max-w-2xl px-[clamp(16px,4vw,48px)]">
                <SomaticResetStrip />
              </div>

              <div className="mx-auto mt-12 w-full max-w-4xl px-[clamp(16px,4vw,48px)]">
                <UpcomingStrip />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
